import { typedKeys } from 'utils/funcs';
import {
  CustomDataFieldType,
  InternalCustomDataField,
  InternalCustomDataFieldSubType,
} from 'models/CustomDataField';
import { PreArrivalFormFields } from './PreArrivalFormTab.types';

interface StaticDefaultField
  extends Pick<
    InternalCustomDataField,
    | 'type'
    | 'subType'
    | 'name'
    | 'variable'
    | 'isOptional'
    | 'isActive'
    | 'minAgeToShow'
  > {}

export type DefaultFieldsMap = Record<string, StaticDefaultField>;

export const DEFAULT_FIELDS_PRIMARY_GUEST: DefaultFieldsMap = {
  [PreArrivalFormFields.PRIMARY_GUEST_FIRST_NAME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Guest First Name',
    variable: '[$GUEST_FIRST_NAME$]',
    isOptional: true,
    isActive: true,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_LAST_NAME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Guest Last Name',
    variable: '[$GUEST_LAST_NAME$]',
    isOptional: true,
    isActive: true,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_EMAIL]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Guest Email',
    variable: '[$GUEST_EMAIL$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_PHONE_NUMBER]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Phone Number',
    variable: '[$PHONE_NUMBER$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_AGE]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Guest Age',
    variable: '[$GUEST_AGE$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_GOVERNMENT_ID]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Government ID',
    variable: '[$GOVERNMENT_ID$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.PRIMARY_GUEST_NUMBER_OF_GUESTS]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.PRIMARY_GUEST_INFORMATION,
    name: 'Number of Guest',
    variable: '[$NUMBER_OF_GUEST$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
} as const;

export const DEFAULT_FIELDS_OTHER_GUEST: DefaultFieldsMap = {
  [PreArrivalFormFields.OTHER_GUEST_FIRST_NAME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest First Name',
    variable: '[$EXTRA_GUEST_FIRST_NAME$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_GUEST_LAST_NAME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest Last Name',
    variable: '[$EXTRA_GUEST_LAST_NAME$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_GUEST_EMAIL]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest Email',
    variable: '[$EXTRA_GUEST_EMAIL$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_GUEST_PHONE_NUMBER]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest Phone Number',
    variable: '[$EXTRA_GUEST_PHONE_NUMBER$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_GUEST_AGE]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest Age',
    variable: '[$EXTRA_GUEST_AGE$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_GUEST_GOVERNMENT_ID]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER_GUEST_INFORMATION,
    name: 'Extra Guest Government ID',
    variable: '[$EXTRA_GUEST_GOVERNMENT_ID$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
} as const;

export const DEFAULT_FIELDS_ARRIVAL_INFORMATION: DefaultFieldsMap = {
  [PreArrivalFormFields.ARRIVAL_FLIGHT_NUMBER]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.ARRIVAL_INFORMATION,
    name: 'Flight Number',
    variable: '[$FLIGHT_NUMBER$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.ARRIVAL_ESTIMATED_ARRIVAL_TIME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.ARRIVAL_INFORMATION,
    name: 'Estimated arrival time',
    variable: '[$ESTIMATED_ARRIVAL_TIME$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.ARRIVAL_ESTIMATED_DEPARTURE_TIME]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.ARRIVAL_INFORMATION,
    name: 'Estimated departure time',
    variable: '[$ESTIMATED_DEPARTURE_TIME$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.ARRIVAL_REASON_FOR_TRIP]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.ARRIVAL_INFORMATION,
    name: 'Reason for your trip',
    variable: '[$REASON_FOR_YOUR_TRIP$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
} as const;

export const DEFAULT_FIELDS_OTHER: DefaultFieldsMap = {
  [PreArrivalFormFields.OTHER_HOSTING_EVENTS]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER,
    name: 'Will you be hosting any gatherings, birthdays, parties or events?',
    variable:
      '[$WILL_YOU_BE_HOSTING_ANY_GATHERINGS_BIRTHDAYS_PARTIES_OR_EVENTS$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_HAS_VISITORS]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER,
    name: 'Will anyone else be visiting you during your stay?',
    variable: '[$WILL_ANYONE_ELSE_BE_VISITING_YOU_DURING_YOUR_STAY$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
  [PreArrivalFormFields.OTHER_AGREE_HOUSE_RULES]: {
    type: CustomDataFieldType.TEXT,
    subType: InternalCustomDataFieldSubType.OTHER,
    name: 'Agree with house rules',
    variable: '[$AGREE_WITH_HOUSE_RULES$]',
    isOptional: true,
    isActive: false,
    minAgeToShow: null,
  },
} as const;

export const OTHER_GUEST_FIELDS = typedKeys(DEFAULT_FIELDS_OTHER_GUEST);

export const ARRIVAL_INFORMATION_FIELDS = typedKeys(
  DEFAULT_FIELDS_ARRIVAL_INFORMATION,
);

export const FIELDS_WITH_MIN_AGE_TO_SHOW = [
  PreArrivalFormFields.OTHER_GUEST_EMAIL,
  PreArrivalFormFields.OTHER_GUEST_GOVERNMENT_ID,
] as const;

export const MIN_AGE_TO_SHOW = 12;
